import { defineStore } from 'pinia';
import useTenantSubscriptionStore from '@/apps/billing-shared/src/store/tenant-subscription.js';
import { useAddOnStore } from '@apps-once/store/add-on';
import { usePlansStore } from '@apps-billing-shared/store/plans';
import { useOnceStore } from '@apps-once/store';
import {
  countryCodes,
  PAYMENT_FREQUENCY,
} from '@/apps/billing-shared/src/definitions';
import AusIcon from '@apps-billing-shared/assets/currency/aus.png';
import GbIcon from '@apps-billing-shared/assets/currency/gb.png';
import UsaIcon from '@apps-billing-shared/assets/currency/usa.png';

const usePricingStore = defineStore('once:pricing', {
  state: () => ({
    planPricing: DP.price_wizard?.getPriceTable(),
    addOnPricing: DP.price_wizard?.getAddOnPriceTable(),
    currency: DP.price_wizard?.currencyTable,
  }),
  getters: {
    isAddOnAvailableForCountry: state => {
      return (country, addOnId) => {
        if (state && state.addOnPricing) {
          const countryPricing = DP.price_wizard.getAddOnPriceByCountry(
            country
          );
          // Check if the add-on has pricing for this country
          if (countryPricing && countryPricing[addOnId]) {
            return true;
          }

          // If not available in this country, check if it's available globally
          const addOnStore = useAddOnStore();
          if (country !== 'US' && addOnStore.isAvailableGlobally(addOnId)) {
            return true;
          }
        }
        return false;
      };
    },
  },
  actions: {
    getPlanPricing(country, subscriptionId) {
      const onceStore = useOnceStore();
      const zuoraCurrency = onceStore.zuoraCurrency;
      let pricingCountry = countryCodes.US; // Defaults to US pricing

      if (!!zuoraCurrency) {
        const tempCountry = this.getPlanPricingCountryFromCurrency(
          zuoraCurrency
        );
        if (tempCountry) {
          return this.planPricing[tempCountry][subscriptionId];
        }
      }

      if (this.planPricing[country]) {
        pricingCountry = country;
      } else if (
        this.currency[country]?.currency &&
        this.currency[country]?.currency !== this.currency[countryCodes.US]
      ) {
        // If country isn't among pricing plans,
        // Retrieve plan pricing from countries with the similar currency.
        // e.g. NZ has 'AUD' as currency. In planPricing, that can be found under 'AU'.
        // However, we can skip this if the country's currency is also USD.
        pricingCountry = this.getPlanPricingCountryFromCurrency(
          this.currency[country].currency
        );
      }

      return this.planPricing[pricingCountry]?.[subscriptionId];
    },
    getPlanPricingCountryFromCurrency(currencyQuery) {
      const planPricingCountries = Object.keys(this.planPricing);
      const pricingExistingIndex = planPricingCountries.findIndex(
        planPricingCountry => {
          if (!this.planPricing[planPricingCountry]) {
            return false;
          }

          const keys = Object.keys(this.planPricing[planPricingCountry]);
          return (
            this.planPricing[planPricingCountry]?.[keys[0]]?.currency ===
            currencyQuery
          );
        }
      );

      if (pricingExistingIndex <= -1) {
        return;
      }

      return planPricingCountries[pricingExistingIndex];
    },
    getAnnualDiscountPercentage(country, annualPlanId) {
      const monthlyCounterpart = usePlansStore().getRelatedMonthlyPlan(
        annualPlanId
      );

      if (!monthlyCounterpart) {
        return 0;
      }

      const annualPrice = this.getPlanPricing(country, annualPlanId).price;
      const monthlyPrice = this.getPlanPricing(country, monthlyCounterpart)
        .price;

      const discountPercentage =
        (1 - annualPrice / monthlyPrice).toPrecision(10) * 100;

      return Math.floor(discountPercentage);
    },
    getAnnualDiscountAmount(country, annualPlanId, userCount) {
      const monthlyCounterpart = usePlansStore().getRelatedMonthlyPlan(
        annualPlanId
      );

      if (!monthlyCounterpart) {
        return 0;
      }

      const annualPrice = this.getPlanPricing(country, annualPlanId).price;
      const monthlyPrice = this.getPlanPricing(country, monthlyCounterpart)
        .price;

      const monthlyDiscountAmount = (monthlyPrice - annualPrice) * userCount;
      const discountAmount = monthlyDiscountAmount * 12;
      return discountAmount.toFixed(2);
    },

    /**
     * Get the annual add-on discount amount.
     * @param {string} country - The country code.
     * @param {string} annualPlanId - The ID of the annual plan.
     * @param {string} annualAddOnId - The ID of the annual add-on.
     * @param {number} userCount - The number of users.
     * @return {number} The annual add-on discount amount.
     */
    getAnnualAddOnDiscountAmount(
      country,
      annualPlanId,
      annualAddOnId,
      userCount
    ) {
      const addOnStore = useAddOnStore();
      const monthlyEquivalentAddOnId = addOnStore.getAddOnEquivalentPaymentFrequencyPlanIdentifier(
        annualAddOnId,
        PAYMENT_FREQUENCY.MONTHLY
      );

      if (!monthlyEquivalentAddOnId) {
        return 0;
      }

      const annualPricingInfo = this._getAddOnPricingInfo(
        country,
        annualPlanId,
        annualAddOnId
      );
      const monthlyPricingInfo = this._getAddOnPricingInfo(
        country,
        annualPlanId,
        monthlyEquivalentAddOnId
      );

      if (
        !annualPricingInfo?.pricing?.price ||
        !monthlyPricingInfo?.pricing?.price
      ) {
        return 0;
      }

      const annualPrice = annualPricingInfo.pricing.price;
      const monthlyPrice = monthlyPricingInfo.pricing.price;

      const monthlyDiscountAmount = (monthlyPrice - annualPrice) * userCount;
      const discountAmount = monthlyDiscountAmount * 12;

      return Number(discountAmount.toFixed(2));
    },

    /**
     * Get the annual add-on savings amount per month.
     *
     * @param {string} country - The country code.
     * @param {string} annualPlanId - The ID of the annual plan.
     * @param {string} annualAddOnId - The ID of the annual add-on.
     *
     * @return {number} The annual add-on savings amount per month.
     */
    getAnnualAddOnMonthlySavingsAmount(country, annualPlanId, annualAddOnId) {
      const addOnStore = useAddOnStore();
      const monthlyEquivalentAddOnId = addOnStore.getAddOnEquivalentPaymentFrequencyPlanIdentifier(
        annualAddOnId,
        PAYMENT_FREQUENCY.MONTHLY
      );

      if (!monthlyEquivalentAddOnId) {
        return 0;
      }

      const annualPricing = this.getAddOnPricing(
        country,
        annualPlanId,
        annualAddOnId
      );
      const annualAddOnPrice = annualPricing?.pricing?.price || 0;

      const monthlyPricing = this.getAddOnPricing(
        country,
        annualPlanId,
        monthlyEquivalentAddOnId
      );
      const monthlyAddOnPrice = monthlyPricing?.pricing?.price || 0;

      const monthlyDiscountAmount =
        (monthlyAddOnPrice - annualAddOnPrice) / monthlyAddOnPrice;
      const discountAmount = monthlyDiscountAmount * 100;

      return Number(discountAmount.toFixed(2));
    },

    /**
     * Get the annual add-on discount percentage.
     *
     * @param {string} country - The country code.
     * @param {string} annualPlanId - The ID of the annual plan.
     * @param {string} addOnId - The ID of the add-on.
     *
     * @return {number} The annual add-on discount percentage.
     */
    getAnnualAddOnDiscountPercentage(country, annualPlanId, addOnId) {
      const {
        annualAddOnPrice,
        monthlyAddOnPrice,
      } = this._getAddOnAnnualAndMonthlyPricingInfo(
        country,
        annualPlanId,
        addOnId
      );

      if (monthlyAddOnPrice === 0 || annualAddOnPrice === 0) {
        return 0;
      }

      const discountPercentage =
        (1 - annualAddOnPrice / monthlyAddOnPrice) * 100;
      return Math.ceil(discountPercentage);
    },

    /**
     * Helper function to get add-on pricing information.
     * @private
     * @param {string} country - The country code.
     * @param {string} planId - The plan ID.
     * @param {string} addOnId - The add-on ID.
     * @return {{ pricing: Object, currency: Object }|undefined} The pricing information.
     */
    _getAddOnPricingInfo(country, planId, addOnId) {
      if (
        this.currency &&
        this.currency[country] &&
        this.addOnPricing &&
        this.addOnPricing[country] &&
        this.addOnPricing[country][addOnId] &&
        this.addOnPricing[country][addOnId][planId]
      ) {
        return {
          pricing: this.addOnPricing[country][addOnId][planId],
          currency: this.currency[country],
        };
      }
      return undefined;
    },

    /**
     * Helper function to get annual and monthly add-on pricing information.
     * @private
     * @param {string} country - The country code.
     * @param {string} annualPlanId - The ID of the annual plan.
     * @param {string} addOnId - The ID of the add-on.
     * @return {{ annualAddOnPrice: number, monthlyAddOnPrice: number }} Object containing the pricing information.
     */
    _getAddOnAnnualAndMonthlyPricingInfo(country, annualPlanId, addOnId) {
      const addOnStore = useAddOnStore();
      const addOns = addOnStore.getRelatedAddOnsOrJustTheAddOn(addOnId);

      let monthlyAddOnPrice = 0;
      let annualAddOnPrice = 0;

      for (const [addOnId, addOn] of Object.entries(addOns)) {
        const pricingInfo = this._getAddOnPricingInfo(
          country,
          annualPlanId,
          addOnId
        );
        const addOnPrice = pricingInfo?.pricing?.price || 0;
        if (addOn?.paymentFrequency === PAYMENT_FREQUENCY.MONTHLY) {
          monthlyAddOnPrice = addOnPrice;
        } else if (
          addOn?.paymentFrequency === PAYMENT_FREQUENCY.ANNUAL_UPFRONT ||
          addOn?.paymentFrequency === PAYMENT_FREQUENCY.ANNUAL_PAID_MONTHLY
        ) {
          annualAddOnPrice = addOnPrice;
        }
      }

      return { annualAddOnPrice, monthlyAddOnPrice };
    },
    getPlanPrice(country, subscriptionId) {
      return this.getPlanPrice(country, subscriptionId);
    },
    getPlanMinimumSpend(country, subscriptionId) {
      return this.getPlanPricing(country, subscriptionId)?.minimumSpend;
    },
    getCurrency(country) {
      if (this.currency[country]) {
        return this.currency[country];
      }
      return this.currency[countryCodes.US];
    },
    getCurrencySymbol(country) {
      const currency = this.getCurrency(country);

      return currency.symbol;
    },
    getCurrencyIcon(country) {
      const currency = this.getCurrency(country);
      if (currency.currency === 'AUD') {
        return AusIcon;
      }
      if (currency.currency === 'GBP') {
        return GbIcon;
      }

      return UsaIcon;
    },
    getPlanPriceSymbol(country, subscriptionId) {
      // TODO: Retire this function as we move to using getCurrencySymbol (detached from plan)
      return this.getPlanPricing(country, subscriptionId).symbol;
    },
    getTaxRate(country) {
      const currency = this.getCurrency(country);

      return currency?.taxRate;
    },
    getTaxId(country) {
      const currency = this.getCurrency(country);

      return currency.tax;
    },
    hasTaxPrice(country, subscriptionId) {
      if (country === countryCodes.NZ) {
        return false;
      }
      return !!this.getPlanPricing(country, subscriptionId)?.taxRate;
    },
    getTaxDetailsLabel(country, subscriptionId) {
      const hasTax = this.hasTaxPrice(country, subscriptionId);

      if (!hasTax) {
        return null;
      }

      return `${this.getTaxId(country)} ${this.getTaxRate(country) * 100}%`;
    },
    getWeeklyCharge(country, subscriptionId) {
      return this.getPlanPricing(country, subscriptionId)?.weeklyCharge;
    },
    getWeeklyInactiveCharge(country, subscriptionId) {
      return this.getPlanPricing(country, subscriptionId)?.weeklyInactiveCharge;
    },
    getMinimumUserCount(country, planId) {
      const planPricing = this.getPlanPricing(country, planId);
      return planPricing?.minimumUsers ?? 1;
    },
    getAddOnPricing(country, planId, addOnId) {
      const tenantSubscriptionStore = useTenantSubscriptionStore();
      const hasCustomPricing = tenantSubscriptionStore.hasCustomPricing();
      if (hasCustomPricing) {
        return undefined;
      }

      const pricingInfo = this._getAddOnPricingInfo(country, planId, addOnId);
      if (pricingInfo) {
        const annualPercentageDiscount = this.getAnnualAddOnDiscountPercentage(
          country,
          planId,
          addOnId
        );

        return {
          ...pricingInfo,
          annualPercentageDiscount,
        };
      }

      const addOnStore = useAddOnStore();
      if (country !== 'US' && addOnStore.isAvailableGlobally(addOnId)) {
        return this.getAddOnPricing('US', planId, addOnId);
      }

      return undefined;
    },
    getAddOnPeriod(country, planId, addOnId) {
      const addOnPricing = this.getAddOnPricing(country, planId, addOnId);
      if (!addOnPricing) {
        return undefined;
      }
      return addOnPricing.pricing?.period;
    },

    /**
     * Get the first day of the next month from today.
     * @return {Date} A Date object representing the 1st day of the next month.
     */
    getNextMonthFirstDay() {
      const today = new Date();
      return new Date(today.getFullYear(), today.getMonth() + 1, 1);
    },

    /**
     * Calculates the number of full months between two dates (in YYYY-MM-DD format).
     *
     * - If the day in the `toDate` is earlier in the month than the `fromDate`,
     *   it counts as one month less (i.e. does not round up).
     * - The result is clamped between 1 and 12 months.
     *
     * @param {string} fromDate - The start date in `YYYY-MM-DD` format.
     * @param {string} toDate - The end date in `YYYY-MM-DD` format.
     *
     * @return {number} The number of full months difference, between 1 and 12.
     *
     * @throws {Error} If either date is invalid or if the difference is less than 1 month.
     *
     * @example
     * getMonthsDifference('2024-01-15', '2024-04-14'); // returns 2
     * getMonthsDifference('2024-01-15', '2024-04-15'); // returns 3
     * getMonthsDifference('2024-01-15', '2025-06-01'); // returns 12
     */
    getMonthsDifference(fromDate, toDate) {
      const start = new Date(fromDate);
      const end = new Date(toDate);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        throw new Error('Invalid date format. Use YYYY-MM-DD.');
      }

      let months = (end.getFullYear() - start.getFullYear()) * 12;
      months += end.getMonth() - start.getMonth();

      // Adjust if end day is before start day in the month
      if (end.getDate() < start.getDate()) {
        months -= 1;
      }

      if (months < 1) {
        console.error('Invalid months difference');
        throw new Error('Months difference must be at least 1');
      }

      return Math.min(months, 12);
    },

    /**
     * Calculates the pro rata upfront price for an annual add-on.
     *
     * This function multiplies the monthly add-on price by the number of months remaining
     * in the base plan and the number of users.
     *
     * @param {string} country - Country code (e.g. 'AU', 'US', etc.)
     * @param {string} planId - Identifier of the base plan.
     * @param {string} addOnId - Identifier of the add-on.
     * @param {number} userCount - Number of users to apply the pricing to.
     * @param {string} annualBasePlanStartDate - The start date of the annual base plan (YYYY-MM-DD).
     * @param {string} annualBasePlanEndDate - The end date of the annual base plan (YYYY-MM-DD).
     *
     * @return {number} The calculated pro rata upfront price.
     *
     * @throws {Error} If add-on pricing is not found or if the month difference is invalid.
     *
     * @example
     * getAnnualAddOnProRataUpfrontPrice('AU', 'plan-premium', 'hr-suite-annual', 10, '2024-01-01', '2024-12-31');
     * // returns 300 if price is $2.5/month, 12 months left, and 10 users
     */
    getAnnualAddOnProRataUpfrontPrice(
      country,
      planId,
      addOnId,
      userCount,
      annualBasePlanStartDate,
      annualBasePlanEndDate
    ) {
      const addOnPricing = this.getAddOnPricing(country, planId, addOnId);
      if (!addOnPricing) {
        console.error('Add-on pricing not found');
        throw new Error('Add-on pro-rata upfront pricing not found');
      }

      // Extract and validate the add-on price
      const { pricing } = addOnPricing;
      const rawPrice = pricing?.price;
      if (rawPrice == null || isNaN(Number(rawPrice))) {
        throw new Error('Invalid pricing data for add-on');
      }
      const price = Number(rawPrice);

      const monthsRemaining = this.getMonthsDifference(
        annualBasePlanStartDate,
        annualBasePlanEndDate
      );

      return price * monthsRemaining * userCount;
    },
  },
});

export { usePricingStore };
